import {
  Button,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { SubmitEvent } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "../dayjs";
import type { IncomingTransfer } from "./useIncomingTransfers";
import { useIncomingTransfers } from "./useIncomingTransfers";
import type { IncomingTransfersSearchCriteria } from "./IncomingTransfersSearch";
import IncomingTransfersSearch from "./IncomingTransfersSearch";
import { useQueryClient } from "@tanstack/react-query";
import { reassignIncomingTransfer } from "./incomingTransfersApi";
import LaunchIcon from "@mui/icons-material/Launch";
import { copyToClipboard } from "../misc/copyToClipboard";

export function IncomingTransfersPage() {
  const [search, setSearch] = useState<IncomingTransfersSearchCriteria>({
    startDate: dayjs().startOf("month").subtract(1, "month"),
  });
  const [reassignStatus, setReassignStatus] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: incomingTransfers, isFetching } = useIncomingTransfers({
    startDate: search.startDate.startOf("month"),
  });

  const handleSearchChange = (value: IncomingTransfersSearchCriteria) => {
    setSearch(value);
  };

  async function reassign(e: SubmitEvent<HTMLFormElement>, it: IncomingTransfer) {
    e.preventDefault();
    setReassignStatus("Przypisuje...");
    try {
      const orderIdInput = e.target.elements.namedItem("orderId") as HTMLInputElement;
      const userFriendlyOrderId = Number(orderIdInput?.value.trim());

      await reassignIncomingTransfer(it.id, userFriendlyOrderId);
      await queryClient.invalidateQueries({
        queryKey: ["incomingTransfers"],
      });
      setReassignStatus("Przypisano pomyślnie.");
    } catch (error) {
      setReassignStatus((error as Error).message);
    }
  }

  return (
    <Stack
      spacing={1}
      sx={{
        marginTop: 1,
        height: "100%",
      }}
    >
      <IncomingTransfersSearch search={search} onChange={handleSearchChange} />
      {isFetching && <LinearProgress />}
      {reassignStatus && (
        <Typography
          sx={{
            paddingX: 2,
          }}
        >
          {reassignStatus}
        </Typography>
      )}
      <TableContainer component={Paper}>
        <Table stickyHeader aria-label="incoming transfers table">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Kwota</TableCell>
              <TableCell>Płatnik</TableCell>
              <TableCell>Tytuł</TableCell>
              <TableCell>Przypisanie</TableCell>
              <TableCell>Konto</TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {incomingTransfers?.map((it: IncomingTransfer) => (
              <TableRow
                key={it.id}
                title={JSON.stringify(it, null, 2)}
                style={{ backgroundColor: it.processingResult.status !== "success" ? "#ffaaaa" : undefined }}
              >
                <TableCell
                  style={{ whiteSpace: "pre-line", cursor: "copy" }}
                  onClick={() => copyToClipboard(JSON.stringify(it, null, 2))}
                >
                  {it.date.replace(/T/, "\n").replace(/:00[.][^.]+$/, "")}
                </TableCell>
                <TableCell>
                  {it.tx.transaction?.balance} {it.tx.header.currency}
                </TableCell>
                <TableCell style={{ whiteSpace: "pre-line" }}>{it.tx.transaction?.payer}</TableCell>
                <TableCell style={{ whiteSpace: "pre-line" }}>{it.tx.transaction?.details}</TableCell>
                <TableCell>{it.processingResult.reason}</TableCell>
                <TableCell>{it.account.replace(/PEKAO_/, "")}</TableCell>
                <TableCell>
                  <form onSubmit={(e) => reassign(e, it)} style={{ display: "flex", gap: "8px" }}>
                    <TextField
                      name="orderId"
                      defaultValue={it.processingResult?.orderId?.userFriendlyId ?? ""}
                      label="Order ID"
                      size="small"
                      type="number"
                      sx={{ width: "100px" }}
                    />
                    <Button type="submit" variant="outlined" size="small">
                      Przypisz
                    </Button>
                    {it.processingResult?.orderId?.id && (
                      <Link to={`/orders/${it.processingResult.orderId.id}`}>
                        <LaunchIcon />
                      </Link>
                    )}
                  </form>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
